import type { Pedido } from "@apiInclui/pedido";
import { motion } from "motion/react";
import { Badge, cn } from "visuais";
import React from "react";
import { IconCurrencyDollar, IconCircleCheckFilled } from "@tabler/icons-react";
import {
  getContentConfig,
  renderInfoItem,
  getStatusClasses,
} from "@PN/inclusao";

interface PedidoContentDialogProps {
  opt: Pedido;
  isSelected: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const PedidoContentDialog = ({
  opt,
  isSelected,
  onMouseEnter,
  onMouseLeave,
}: PedidoContentDialogProps) => {
  const prefixoDocumento =
    (opt.tipoDocumento || "").toUpperCase() === "AE"
      ? "Autorização"
      : "Pedido";

  // Obter configurações dos itens
  const { topRowItems, middleRowItems, bottomRowItems } = getContentConfig(opt);

  return (
    <motion.div
      className={cn(
        "w-full gap-2 group cursor-pointer transition-all duration-150 flex flex-col"
      )}
      whileHover={{ scale: 1.005 }}
      transition={{ duration: 0.15 }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header do pedido */}
      <div className="flex w-full justify-between">
        <div className="flex items-center gap-2 w-full">
          {/* Icone de seleção */}
          <div
            className={cn(
              "flex items-center justify-center w-4 h-4 rounded-full border transition-all duration-150",
              isSelected
                ? "border-cyan-500 bg-cyan-100 dark:bg-cyan-900/30"
                : "border-slate-300 dark:border-slate-600"
            )}
          >
            {isSelected ? (
              <IconCircleCheckFilled className="h-2.5 w-2.5 text-cyan-600 dark:text-cyan-400" />
            ) : (
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400 group-hover:bg-slate-600 transition-colors duration-150" />
            )}
          </div>
          <span className="font-medium text-sm">
            {prefixoDocumento} {opt.codPedido}
          </span>
          {/* Status do Pedido */}
          <Badge variant={"outline"} className={getStatusClasses(opt.status)}>
            {opt.status || "N/A"}
          </Badge>
        </div>

        {/* Valor Total */}
        <Badge
          variant={"outline"}
          className="text-[9px] px-1.5 py-0.5 font-medium bg-foreground/10 dark:bg-foreground/20 text-foreground border-foreground/20 dark:border-foreground/30"
        >
          <IconCurrencyDollar className="h-2.5 w-2.5 mr-1" />
          {(opt.valorTotal || 0).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
          })}
        </Badge>
      </div>

      <div className="w-full flex flex-col gap-1">
        {/* Observação do Pedido */}
        {opt.observacao && (
          <div className="w-full px-0.5">
            <p className="text-[8px] text-muted-foreground/80 italic truncate leading-tight">
              💬 {opt.observacao}
            </p>
          </div>
        )}

        {/* Linha de Cima */}
        <div className="w-full flex justify-between gap-2">
          {topRowItems.map((item, index) => renderInfoItem(item, index))}
        </div>

        {/* Linha do Meio */}
        <div className="w-full flex justify-between gap-2">
          {middleRowItems.map((item, index) => renderInfoItem(item, index))}
        </div>

        {/* Linha de baixo */}
        <div className="w-full flex justify-between">
          {bottomRowItems.map((item, index) => renderInfoItem(item, index))}
        </div>
      </div>
    </motion.div>
  );
};
